import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { Market } from "../market";

/**
 * Data the v3 homepage fetches on the server, so these sections are in the HTML search
 * engines read rather than appearing only after hydration:
 *
 *   reviews       — the Trustpilot review cards ("Trust of 2 Million+ students")
 *   universities  — the "Stays near top universities" pills
 *   stays         — the first university's stays, keyed by its canonical name
 *   market        — the visitor's market, from their country on the request (see market.ts)
 *
 * Each is null until fetched; the components fetch in the browser only while theirs is
 * still null (a failed or skipped server fetch), so nothing is requested twice.
 */

export type TpReview = {
  user_name?: string;
  university?: string;
  destination_city?: string;
  trustpilot_rating?: number;
  edited_review?: string;
  [key: string]: unknown;
};

export type University = { name: string; canonicalName: string };

export type StayRow = { item: any; text: string };

export interface HomeV3State {
  reviews: TpReview[] | null;
  universities: University[] | null;
  stays: Record<string, StayRow[]>;
  /** Null when the server couldn't tell; the browser then looks the country up. */
  market: Market | null;
  /**
   * The visitor's ISO country as the server read it (the same header or cookie as the
   * market, before it is narrowed to GB/US/AU). The sign-in's default dialling code.
   */
  country: string | null;
  /**
   * The phone's top app banner: shown unless the visitor dismissed it (the live
   * banner's `showAppBanner` cookie). Read on the server, so the banner is in the HTML
   * from the first paint and the page never jumps when it appears.
   */
  appBanner: boolean;
}

const initialState: HomeV3State = {
  reviews: null,
  universities: null,
  stays: {},
  market: null,
  country: null,
  appBanner: false,
};

const homeV3Slice = createSlice({
  name: "homeV3Data",
  initialState,
  reducers: {
    setHomeV3Reviews: (state, { payload }: PayloadAction<TpReview[]>) => {
      state.reviews = payload;
    },
    setHomeV3Universities: (state, { payload }: PayloadAction<University[]>) => {
      state.universities = payload;
    },
    setHomeV3Stays: (state, { payload }: PayloadAction<{ key: string; rows: StayRow[] }>) => {
      state.stays[payload.key] = payload.rows;
    },
    setHomeV3Market: (state, { payload }: PayloadAction<Market | null>) => {
      state.market = payload;
    },
    setHomeV3Country: (state, { payload }: PayloadAction<string | null>) => {
      state.country = payload;
    },
    setHomeV3AppBanner: (state, { payload }: PayloadAction<boolean>) => {
      state.appBanner = payload;
    },
  },
});

export const {
  setHomeV3Reviews,
  setHomeV3Universities,
  setHomeV3Stays,
  setHomeV3Market,
  setHomeV3Country,
  setHomeV3AppBanner,
} = homeV3Slice.actions;

export default homeV3Slice.reducer;
